import axios from 'axios';
import { useEffect, useState } from 'react';

interface DimensionsResponse {
  data: { key: string }[];
}

/**
 * Dimension keys the current caller may actually query, per `GET
 * /reporting-2.0/dimensions`. flank#4644 made this permission-aware
 * server-side: `app_name`/`domain`/`publisher` are omitted entirely for a
 * user without `can_view_raw_inventory`. This is what flank#4643 builds the
 * Inventory tab's raw App/Domain picker from, rather than a hardcoded chip
 * list, so the alternate view disappears for a disallowed user with no
 * flank-side permission check of its own.
 *
 * Cached at module scope — the set is effectively static for a given login,
 * and every consumer (today: the Inventory tab) wants the same answer.
 */
let cached: Promise<Set<string>> | null = null;

/**
 * Clears the module-scope cache below. Called from logoutCleanup — a
 * logout/login (or whitelabel switch) doesn't reload the page, and this
 * cache has no other way to learn the permission set it answered for is
 * stale (review, CodeRabbit).
 */
export const clearAvailableDimensionsCache = (): void => {
  cached = null;
};

const fetchAvailableDimensions = (): Promise<Set<string>> => {
  if (!cached) {
    cached = axios
      .get<DimensionsResponse>('/reporting-2.0/dimensions')
      .then(res => new Set((res.data?.data ?? []).map(d => d.key)))
      .catch(() => {
        // Don't let a transient failure become a permanent empty result for
        // the rest of the session — the next caller should retry rather than
        // keep the App/Domain chips hidden until a full page reload.
        cached = null;
        return new Set<string>();
      });
  }
  return cached;
};

/**
 * Starts empty (fails closed — a chip gated on this set stays hidden until
 * the fetch resolves, never briefly shown then pulled).
 */
export const useAvailableDimensions = (): Set<string> => {
  const [dims, setDims] = useState<Set<string>>(new Set());

  useEffect(() => {
    let active = true;
    fetchAvailableDimensions().then(result => {
      if (active) setDims(result);
    });
    return () => {
      active = false;
    };
  }, []);

  return dims;
};
